import * as React from 'react';
import { cloneDeep } from 'lodash';
import { API } from '@config/index';
import {
  NiSelect,
  NiButton,
  NiSpin,
  NiTable,
  ReferenceTextArea,
  niConfirm
} from '@component/index';
import { message, Drawer, Dropdown, Menu, Space, Modal, Popover } from 'antd';
import reactJsonView from 'react-json-view';
import { localizedString } from '@locale/index';
import { globalModel, PayloadInfo } from '@reducer/global';
import { DispatchProp } from 'react-redux';
import { frameEventHistory, addNewFrameEvent, instance } from '@lib/index';
import { setTimeout } from 'timers';
import {
  serviceInfoToLocal,
  ServiceInfo,
  DataQuery,
  FunctionParameter,
  FrameType
} from '@interface/index';

import './index.less';


const ReactJson = reactJsonView;

enum FunctionTestingMode {
  FunctionInvocation = 'function_invocation',
  CodeExpression = 'code_expression'
}

const FunctionTestModeText = {
  [FunctionTestingMode.FunctionInvocation]: 'Function Invocation',
  [FunctionTestingMode.CodeExpression]: 'Code Expression'
};

type FunctionTestingDrawerProps = DispatchProp & {
  visible: boolean;
  disabled?: boolean;
  onClose?: () => void;
};
export interface FunctionTestingDrawerState {
  mode: FunctionTestingMode;
  service?: ServiceInfo;
  selectedFuncId?: string;
  parameters: any;
  codeExpression: string;
  result?: any;
  loading: boolean;
}

const defaultState: FunctionTestingDrawerState = {
  mode: FunctionTestingMode.FunctionInvocation,
  parameters: {},
  codeExpression: '',
  loading: false
};

class FunctionTestingDrawerComp extends React.Component<FunctionTestingDrawerProps, FunctionTestingDrawerState> {
  public state: FunctionTestingDrawerState = { ...defaultState };
  $structureInput: any;

  componentDidMount() {
    this.onInit();
  }

  onInit = async () => {
    const service: any = await API.service.getService(undefined, undefined, 'master')
      .then(serviceInfoToLocal)
      .catch((err: Error) => {
        message.error(err.message);
        throw err;
      });

    this.setState({ service });
  }

  getSelectedFunc = () => {
    const { selectedFuncId, service } = this.state;
    return service && (service.dataQueries || []).find((query: DataQuery) => query.id === selectedFuncId);
  }

  render() {
    const { visible, disabled, onClose } = this.props;
    const {
      mode,
      service,
      parameters,
      codeExpression,
      result,
      selectedFuncId,
      loading
    } = this.state;

    const selectedFunc = this.getSelectedFunc();

    return (

      <Drawer
        className="function-testing-drawer"
        open={visible}
        width={550}
        closable={false}
        onClose={() => {
          if (onClose) {
            onClose();
          }
        }}
        title={
          <div className="function-testing-header">
            <span>Function Console</span>
            <NiButton
              className="execute-button"
              onClick={() => {
                setTimeout(this.handleQuery, 500);
              }}
            >
              Execute
            </NiButton>
          </div>
        }
      >
        <NiSpin spinning={loading}>
          {/* <div key="mode" className="mode-wrap">
            <NiRadio
              disabled={disabled}
              style={{
                marginTop: 4
              }}
              value={mode}
              onChange={(e: any) => {
                this.setState({ mode: e.target.value });
              }}
              keyValue={[
                { key: FunctionTestingMode.FunctionInvocation, value: FunctionTestModeText[FunctionTestingMode.FunctionInvocation] },
                { key: FunctionTestingMode.CodeExpression, value: FunctionTestModeText[FunctionTestingMode.CodeExpression] }
              ]}
            />
          </div> */}
          {
            mode === FunctionTestingMode.CodeExpression
              ? <div key="code" className="code-expression-wrap">
                {/* <div style={{ marginBottom: 12 }}>Code Expression</div> */}
                <ReferenceTextArea
                  placeholder="Code Expression"
                  key="code"
                  rawCodeMode
                  data={codeExpression}
                  parameterMap={{}}
                  initParameters={[]}
                  update={(next: string) => {
                    this.setState({ codeExpression: next });
                  }}
                />
              </div>
              : <>
                <div key="func" className="func-wrap">
                  <NiSelect
                    value={selectedFuncId}
                    onChange={(value: string) => {
                      if (value !== this.state.selectedFuncId) {
                        this.setState({
                          selectedFuncId: value, parameters: {}
                        });
                        return;
                      }
                      this.setState({ selectedFuncId: value });
                    }
                    }
                    placeholder="Select a function"
                  >
                    {
                      (service && service.dataQueries || [])
                        .filter((query: DataQuery) => !query.kotlin)
                        .map((query: DataQuery) =>
                          <NiSelect.Option
                            key={query.id || ''}
                            value={query.id || ''}
                          >
                            {query.name}
                          </NiSelect.Option>)
                    }
                  </NiSelect>
                </div>
                <div key="param-table-wrap" className="param-table-wrap">
                  {
                    <NiTable
                      pagination={false}
                      dataSource={selectedFunc && selectedFunc.parameters || []}
                      columns={[
                        {
                          title: 'Name',
                          dataIndex: 'name'
                        },
                        {
                          title: 'Value',
                          key: 'value',
                          render: (_: any, record: FunctionParameter, index: number) => {
                            return <ReferenceTextArea
                              key={record.name}
                              rawCodeMode
                              data={parameters[record.name]}
                              parameterMap={{}}
                              initParameters={[]}
                              update={(next: string) => {
                                const nextParam = cloneDeep(parameters);
                                nextParam[record.name] = next;
                                this.setState({ parameters: nextParam });
                              }}
                            />;
                          }
                        }
                      ]}
                    />
                  }
                </div>
              </>
          }
          <div key="result-wrap">
            <div>Result</div>
            <div className="result-json-wrap">
              {this.renderContent(result)}
            </div>
          </div>
        </NiSpin>
      </Drawer >
    );
  }

  renderContent = (src: any) => {

    if (src === undefined || src === null) {
      return <div className="no-data-wrap">No Data</div>;
    }

    if (typeof src === 'object') {

      return <ReactJson
        src={src}
        name={false}
        displayObjectSize={false}
        displayDataTypes={false}
      />;
    }

    return String(src);
  }

  handleQuery = () => {
    const { dispatch } = this.props;
    const { parameters, mode, codeExpression } = this.state;

    if (mode === FunctionTestingMode.CodeExpression) {
      if (!codeExpression.length) {
        message.warning('Please enter code expression!');
        return;
      }

      this.setState({
        loading: true
      });

      API.service
        .functionTestWithCodeExpression(codeExpression)
        .then((res: any) => {
          this.setState({
            result: res,
            loading: false
          });
        })
        .catch((err: any) => {
          if (err.data) {
            Modal.warning({
              width: '60%',
              content: <pre>{err.data}</pre>
            });
          } else {
            message.warning(err.message);
          }


          this.setState({
            loading: false
          });

          throw new Error(err.message);
        });
      return;
    }


    const selectedFunc = this.getSelectedFunc();

    if (!selectedFunc) {
      message.warning('Please select a function!');
      return;
    }

    this.setState({
      loading: true
    });

    const paramDefs = selectedFunc.parameters;
    const parsedParams: any = {};
    for (const d of paramDefs) {
      const key = d.name;
      const value = parameters[key];

      if (d.type && d.type.name && (d.type.type === FrameType.composite || d.type.name === 'io.opencui.serialization.JsonElement' || d.type.name === 'JsonElement'
        || d.type.name === 'io.opencui.serialization.JsonObject' || d.type.name === 'JsonObject'
      )) {
        try {
          const parsedV = JSON.parse(value as string);
          parsedParams[key] = parsedV;

        } catch (err) {
          if (value && (value as string).length > 0) {
            parsedParams[key] = value;
          } else {
            parsedParams[key] = null;
          }
        }
      } else {
        if (value && (value as string).length > 0) {
          parsedParams[key] = value;
        } else {
          parsedParams[key] = null;
        }
      }

    }

    API.service
      .functionTest(selectedFunc.name || '', parsedParams)
      .then((res: any) => {
        this.setState({
          result: res,
          loading: false
        });
      })
      .catch((err: any) => {
        if (err.message) {
          niConfirm({
            width: '60%',
            title: localizedString('common_error_info'),
            content: <pre style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all'
            }}>{err.message}</pre>
          });
        } else {
          message.warning(err.message);
        }


        this.setState({
          loading: false
        });

        throw new Error(err.message);
      });
  };
}

export const FunctionTestingDrawer = globalModel.connect()(FunctionTestingDrawerComp as any);

